import { createStore } from '@oneappexchange/lwc-wired';
import { State } from 'zustand';
import { InjectedStores } from '~/modules/appx_site_schema';
import { IS_DEVELOPMENT } from '~/modules/config';
import { buildHomeCategorySection, buildHomeListingSection } from '~/modules/helpers/contentFeedHelper';
import { buildHomeContentSections } from '~/modules/helpers/contentSectionHelper';
import { AnalyticsService } from '~/modules/services/analyticsService';
import { handleException } from '~/modules/utilities/error_utilities';
import { Category, Content, Section } from '../../schemas/content-schema-v1';
import { ContentService, SectionsResult } from '../services/contentService';
import { logger } from '../utilities/logger';

export const SPONSORED_FEED_URL = '/home_content/sponsored';

const contentService = new ContentService();
const EXPERIMENT_ID = 'pw7UybmXScyjiI5d61rRKg'; //TODO: this is NOT A SECRET, but will find a better way to inject this, probably env-var

export interface ContentRecords {
    isError: boolean;
    isEmpty: boolean;
    errorMessage: string;
    records: Content[] | Category[];
}

export interface ContentState extends State {
    sections: Section[];
    feeds: {
        category: ContentRecords;
        sponsored: ContentRecords;
        top_trending: ContentRecords;
    };
}

const initialRecordsState = (): ContentRecords => {
    return {
        isError: false,
        // isEmpty needs to be initialized to false, and should only be updated in 'buildFeedState'.
        // It's necessary that it's false, until we validate that the data fetch returned an empty array.
        isEmpty: false,
        errorMessage: '',
        records: [],
    };
};

const initialState: ContentState = {
    sections: [],
    feeds: {
        category: initialRecordsState(),
        sponsored: initialRecordsState(),
        top_trending: initialRecordsState(),
    },
};

export const selectSponsoredFeed = (state: ContentState) => {
    return state.feeds?.sponsored;
};

export const selectCategoryFeed = (state: ContentState): ContentRecords | undefined => {
    return state.feeds?.category;
};

export const contentStore = createStore(
    initialState,

    (set: SetState<ContentState>, get: GetState<ContentState>) => ({
        clearError(section: string) {
            set(function clearError(draft) {
                // @ts-expect-error * Automated Type Error Fix
                draft.feeds[section].isError = false;
                // @ts-expect-error * Automated Type Error Fix
                draft.feeds[section].isEmpty = false;
                // @ts-expect-error * Automated Type Error Fix
                draft.feeds[section].errorMessage = '';
                return draft;
            });
        },
        setError(section: string, error: string) {
            set(function setError(draft) {
                logger.error(`Section ${section} has the following error; ${error}`);
                // @ts-expect-error * Automated Type Error Fix
                if (!draft.feeds[section]) {
                    // @ts-expect-error * Automated Type Error Fix
                    draft.feeds[section] = initialRecordsState();
                }
                // @ts-expect-error * Automated Type Error Fix
                draft.feeds[section].isError = true;
                // @ts-expect-error * Automated Type Error Fix
                draft.feeds[section].isEmpty = false;
                // @ts-expect-error * Automated Type Error Fix
                draft.feeds[section].errorMessage = error;
                return draft;
            });
        },
        setHomeContent(homeContent: InjectedStores['HOME_CONTENT']) {
            set(function setHomeContent(draft: ContentState) {
                // @ts-expect-error * Automated Type Error Fix
                draft.sections = buildHomeContentSections(homeContent.sections);
                // @ts-expect-error * Automated Type Error Fix
                draft.feeds.top_trending = buildFeedState(buildHomeListingSection(homeContent.feed.top_trending));
                // @ts-expect-error * Automated Type Error Fix
                draft.feeds.sponsored = buildFeedState(buildHomeListingSection(homeContent.feed.sponsored));
                // @ts-expect-error * Automated Type Error Fix
                draft.feeds.category = buildFeedState(buildHomeCategorySection(homeContent.feed.category));
            });
        },
        async fetchSections(isGuestUser: boolean, forceReload = false) {
            const sectionSelector = get().sections;
            const hasRecords = sectionSelector?.length > 0;
            // If login for first time, or was logged and logout, or if logins with a new user => need to fetch again sections

            if (!hasRecords || forceReload) {
                const { sections, headers }: SectionsResult = await contentService.getSections(isGuestUser);
                const analyticsService = new AnalyticsService();

                const variant = headers?.in_ab_position_test_secondary === 'true' ? 1 : 0;
                analyticsService.sendExperimentEvent(EXPERIMENT_ID, variant).catch();
                set(function setSections(draft: ContentState) {
                    draft.sections = sections;
                });
            }
        },
        async fetchFeeds(url: string, storeName: string) {
            try {
                // @ts-expect-error * Automated Type Error Fix
                const feedSelector = get().feeds[storeName];
                const hasRecords = feedSelector?.records?.length > 0 || feedSelector?.isEmpty;

                if (!hasRecords) {
                    try {
                        const result = await contentService.getFeeds(url);

                        set(function updateSections(draft: ContentState) {
                            // @ts-expect-error * Automated Type Error Fix
                            draft.feeds[storeName] = buildFeedState(result);
                        });
                    } catch (error) {
                        handleException(error, 'contentStore.fetchFeeds');
                        this.setError(storeName, error);
                    }
                }
            } catch (e) {
                logger.error(`Failed fetching feeds: ${e}`);
            }
        },
        reset() {
            set(function resetStore() {
                return initialState;
            });
        },
    }),
    'content',
    IS_DEVELOPMENT,
);

function buildFeedState(data: Content[] | Category[]) {
    const state = initialRecordsState();
    state.records = data;
    // isEmpty should only be set here, and nowhere else in this file.
    // It's necessary that it's false, until we validate that the data fetch returned an empty array.
    state.isEmpty = data && data.length === 0;
    return state;
}

export const fetchSponsoredFeed = () => {
    contentStore.fetchFeeds(SPONSORED_FEED_URL, 'sponsored');
};

export const selectAmpCampaign = (state: ContentState, currentListingId: string, placementId: string) => {
    const sponsoredListings = state.feeds.sponsored.records as Content[];
    const sponsoredListing = sponsoredListings.find((record) => record.id === currentListingId);
    // @ts-expect-error * Automated Type Error Fix
    const sponsoredCampaign = sponsoredListing?.sponsoredCampaigns.find(
        (campaign) => campaign.placement_id === placementId,
    );
    if (sponsoredCampaign) {
        return { sponsoredCampaign };
    }
    return {};
};
